import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import { RoundedButton } from '@/components/shared/RoundedButton';
import { getSongThemeColor } from '@/components/shared/SongThemeChip';
import { ChartSectionData } from '@/helpers/reportsDataHelpers';
import { TagTrendPoint } from '@/hooks/useReportsData';
import { down } from '@/assets/icons';
import { SvgIcon } from '@material-ui/core';
import { ResponsiveStream } from '@nivo/stream';

import { ChartEmptyState } from '../ChartEmptyState/ChartEmptyState';
import styles from './TrendLineChart.module.scss';

// Hues ordered by closeness to blue (blue at centre, radiating outward).
// Each column = shade at that index: 0→300, 1→400, 2→200, 3→500, 4→100.
const HUE_SHADES: string[][] = [
	['#85c7ff', '#56a0ff', '#b3dfff', '#2f79ff', '#d5efff'], // blue
	['#62fbfc', '#1eedf2', '#a3fefe', '#02d1d8', '#cefffe'], // aqua
	['#cec1f1', '#af98e8', '#e3dcf8', '#9071db', '#f0ecfb'], // purple
	['#80ffa1', '#33f566', '#b2ffc5', '#09de41', '#d7ffe1'], // teal
	['#faa7b4', '#f7758d', '#fccfd6', '#ee4568', '#fee5e8'], // maroon
	['#c7ff4e', '#b1f625', '#e1ff95', '#92dd05', '#f1ffc7'], // green
	['#f8c379', '#f4a043', '#fbddad', '#f1831d', '#fef0d6'], // orange
	['#fcef48', '#f9e22c', '#fdf98b', '#e8c60a', '#fefec3'], // yellow
	['#ffaa9c', '#ff7963', '#ffcdc5', '#ff4f32', '#ffe4df'], // red
];

const NUM_HUES = HUE_SHADES.length;

// Shade stages by label count. Each entry is an ordered list of HUE_SHADES column indices,
// strongest first. Stages: ≤9 → [300]; ≤18 → [300,200]; ≤36 → [500,400,300,200]; >36 → all five.
const SHADE_STAGES: number[][] = [
	[0],             // 300
	[0, 2],          // 300, 200
	[3, 1, 0, 2],    // 500, 400, 300, 200
	[3, 1, 0, 2, 4], // 500, 400, 300, 200, 100
];

function getShadeStage(labelCount: number): number[] {
	if (labelCount <= NUM_HUES) return SHADE_STAGES[0];
	if (labelCount <= NUM_HUES * 2) return SHADE_STAGES[1];
	if (labelCount <= NUM_HUES * 4) return SHADE_STAGES[2];
	return SHADE_STAGES[3];
}

function hexToRgba(hex: string, alpha: number): string {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r},${g},${b},${alpha})`;
}

type Props = {
	title: string;
	section: ChartSectionData<TagTrendPoint>;
	semanticColors?: boolean;
};

export const TrendLineChart: React.FC<Props> = ({ title, section, semanticColors = false }) => {
	const [view, setView] = useState<'all' | 'top10'>('all');
	const [anchorEl, setAnchorEl] = useState<Element | null>(null);
	const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);

	// Stable ref so the LayerTooltip component (fixed identity) can always call the current setter.
	const setHoveredRef = useRef(setHoveredLabel);
	setHoveredRef.current = setHoveredLabel;

	const totalsRef = useRef(new Map<string, number>());
	const grandTotalRef = useRef(0);

	// Stable component — uses useLayoutEffect to set/clear hoveredLabel when a band is entered/left.
	// Cleanup uses functional update to avoid clearing a label that was already replaced by a new hover.
	const LayerTooltip = useMemo(() => {
		return function TrendLayerTooltip({ layer }: { layer: { id: string | number; color: string } }) {
			useLayoutEffect(() => {
				const label = String(layer.id);
				setHoveredRef.current(label);
				return () => {
					setHoveredRef.current((prev) => prev === label ? null : prev);
				};
			}, [layer.id]);
			const label = String(layer.id);
			const value = totalsRef.current.get(label) ?? 0;
			const pct = grandTotalRef.current > 0 ? ((value / grandTotalRef.current) * 100).toFixed(1) : '0.0';
			return (
				<div style={{
					background: 'white',
					padding: '8px 12px',
					borderRadius: 4,
					boxShadow: '0 2px 8px rgba(0,0,0,.12)',
					lineHeight: 1.4,
					textAlign: 'center',
					fontSize: 12,
				}}>
					<div style={{ fontWeight: 500 }}>{label} · {pct}%</div>
				</div>
			);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Key order determines layer stacking. Sort by total ascending, then interleave
	// outside-in so the largest bands sit at the outer edges and smallest in the middle.
	// Color rank uses descending order so the highest-weight label gets the best color.
	const { allKeys, colorMap, totals } = useMemo(() => {
		const labels = [...new Set(section.data.map((p) => p.label))];

		const tots = new Map<string, number>();
		for (const point of section.data) {
			tots.set(point.label, (tots.get(point.label) ?? 0) + point.count);
		}

		const sortedAsc = [...labels].sort((a, b) => (tots.get(a) ?? 0) - (tots.get(b) ?? 0));

		const arranged = new Array<string>(sortedAsc.length);
		let left = 0;
		let right = sortedAsc.length - 1;
		for (let i = 0; i < sortedAsc.length; i++) {
			if (i % 2 === 0) arranged[left++] = sortedAsc[i];
			else arranged[right--] = sortedAsc[i];
		}

		const cm = new Map<string, { colorHex: string }>();

		if (semanticColors) {
			labels.forEach((label) => {
				cm.set(label, { colorHex: getSongThemeColor(label).colorHex });
			});
		} else {
			const sortedDesc = [...labels].sort((a, b) => (tots.get(b) ?? 0) - (tots.get(a) ?? 0));
			const activeShadeIndices = getShadeStage(labels.length);
			sortedDesc.forEach((label, rank) => {
				const hueIndex = rank % NUM_HUES;
				const tierIndex = Math.min(Math.floor(rank / NUM_HUES), activeShadeIndices.length - 1);
				cm.set(label, { colorHex: HUE_SHADES[hueIndex][activeShadeIndices[tierIndex]] });
			});
		}

		return { allKeys: arranged, colorMap: cm, totals: tots };
	}, [section.data, semanticColors]);

	totalsRef.current = totals;
	grandTotalRef.current = [...totals.values()].reduce((s, v) => s + v, 0);

	const top10Set = useMemo(() => {
		const sorted = [...allKeys].sort((a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0));
		return new Set(sorted.slice(0, 10));
	}, [allKeys, totals]);

	const keys = useMemo(
		() => view === 'top10' ? allKeys.filter((k) => top10Set.has(k)) : allKeys,
		[view, allKeys, top10Set],
	);

	const colorEntries = useMemo(
		() => keys.map((k) => colorMap.get(k) ?? { colorHex: '#d6d6e7' }),
		[keys, colorMap],
	);

	const colors = useMemo(
		() => keys.map((k, i) => {
			const hex = colorEntries[i]?.colorHex ?? '#d6d6e7';
			if (!hoveredLabel || hoveredLabel === k) return hex;
			return hexToRgba(hex, 0.15);
		}),
		[keys, colorEntries, hoveredLabel],
	);

	const { streamData, dates } = useMemo(() => {
		const orderedDates = [...new Set(section.data.map((p) => p.date))].sort();

		const sliceMap = new Map<string, Record<string, number>>();
		for (const point of section.data) {
			const slice = sliceMap.get(point.date) ?? {};
			slice[point.label] = (slice[point.label] ?? 0) + point.count;
			sliceMap.set(point.date, slice);
		}

		const data: Record<string, number>[] = orderedDates.map((date) => {
			const slice = sliceMap.get(date) ?? {};
			return Object.fromEntries(keys.map((k) => [k, slice[k] ?? 0]));
		});

		return { streamData: data, dates: orderedDates };
	}, [section.data, keys]);

	if (section.emptyReason !== null) {
		return (
			<ModalSectionCard paddingSize="lg">
				<ModalSectionTitle>{title}</ModalSectionTitle>
				<ChartEmptyState copy="Complete a setlist to see this chart." />
			</ModalSectionCard>
		);
	}

	if (dates.length < 2) {
		return (
			<ModalSectionCard paddingSize="lg">
				<ModalSectionTitle>{title}</ModalSectionTitle>
				<ChartEmptyState icon="rows" copy="Add at least 2 setlists to your date range to see trends." />
			</ModalSectionCard>
		);
	}

	const spanDays = dates.length >= 2
		? (new Date(dates[dates.length - 1] + 'T00:00:00').getTime() - new Date(dates[0] + 'T00:00:00').getTime()) / 86400000
		: 0;
	const useMonthlyTicks = spanDays > 90;

	const showToggle = allKeys.length > 10;
	const chevron = <SvgIcon component={down} style={{ fontSize: 14 }} />;

	const toggleChip = showToggle ? (
		<>
			<RoundedButton size="md" trailingComponent={chevron} onClick={(e) => setAnchorEl(e.currentTarget)}>
				{view === 'top10' ? 'Top 10' : 'All'}
			</RoundedButton>
			<AsaphDropdownMenu
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				selectable
				items={[
					{ label: 'All', selected: view === 'all', onClick: () => { setView('all'); setAnchorEl(null); } },
					{ label: 'Top 10', selected: view === 'top10', onClick: () => { setView('top10'); setAnchorEl(null); } },
				]}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			/>
		</>
	) : undefined;

	return (
		<ModalSectionCard paddingSize="lg">
			<ModalSectionTitle sideComponent={toggleChip}>{title}</ModalSectionTitle>
			<div className={styles['chart']}>
				<ResponsiveStream
					data={streamData}
					keys={keys}
					colors={colors}
					offsetType="silhouette"
					curve="natural"
					fillOpacity={0.85}
					borderWidth={0}
					margin={{ top: 25, right: 10, bottom: 35, left: 10 }}
					axisBottom={{
						tickSize: 0,
						tickPadding: 8,
						format: (i: number) => {
							const raw = dates[i];
							if (!raw) return '';
							const prev = dates[i - 1];
							if (prev) {
								if (useMonthlyTicks) {
									if (raw.slice(0, 7) === prev.slice(0, 7)) return '';
								} else {
									const weekOf = (s: string) => Math.floor(new Date(s + 'T00:00:00').getTime() / 604800000);
									if (weekOf(raw) === weekOf(prev)) return '';
								}
							}
							const d = new Date(raw + 'T00:00:00');
							return useMonthlyTicks
								? d.toLocaleDateString('en-US', { month: 'short' })
								: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
						},
					}}
					axisLeft={null}
					enableGridX={false}
					enableGridY={false}
					enableStackTooltip={false}
					tooltip={LayerTooltip}
					theme={{
						axis: { ticks: { text: { fill: '#777aaf' } } },
					}}
				/>
			</div>
			<div className={styles['legend']}>
				{keys.map((k, i) => (
					<div
						key={k}
						className={styles['legend-item']}
						data-active={hoveredLabel === k}
						style={{ opacity: hoveredLabel && hoveredLabel !== k ? 0.3 : 1, cursor: 'default' }}
						onMouseEnter={() => setHoveredLabel(k)}
						onMouseLeave={() => setHoveredLabel(null)}
					>
						<div className={styles['legend-circle']} style={{ background: colorEntries[i].colorHex }} />
						<span className={styles['legend-label']}>{k}</span>
					</div>
				))}
			</div>
		</ModalSectionCard>
	);
};
